<template>
  <BaseTable
    :rows="visibleRows ?? []"
    :columns="columns"
    :is-loading="isLoading"
  >
    <template #cell-name="{ row }">
      <BaseTableChevronLink
        :to="{
          name: 'merchantGroupMerchantDetail',
          params: {
            schemaName: row.schemaName,
          },
        }"
      >
        <p
          :class="
            row.isActive
              ? 'text-background-fg'
              : 'text-background-fg/60'
          "
        >
          {{ row.merchantName }}
          <span v-if="!row.isActive">(disabled)</span>
        </p>
      </BaseTableChevronLink>
    </template>

    <template #cell-portalLink="{ value }">
      <BaseTableButton
        :href="value"
        target="_blank"
        variant="info"
      >
        Open Portal
      </BaseTableButton>
    </template>
  </BaseTable>

  <div class="flex justify-end py-2">
    <BasePagination
      v-model:page="page"
      :page-count="pageCount"
    />
  </div>
</template>

<script setup lang="ts">
import * as DateFns from "date-fns"

import type { TableColumn } from "@/components/BaseTable.vue"
import type { ListMerchantGroupMerchantsResponseItem } from "@/services/merchantGroup/merchants/listMerchantGroupMerchants"

const ROWS_PER_PAGE = 10

const props = defineProps<{
  merchants: ListMerchantGroupMerchantsResponseItem[]
  isLoading: boolean
}>()

const { page, pageCount } = usePagination({
  count: computed(() => props.merchants.length),
  limit: ROWS_PER_PAGE,
})

const visibleRows = computed(() =>
  props.merchants.slice(
    (page.value - 1) * ROWS_PER_PAGE,
    page.value * ROWS_PER_PAGE
  )
)

const now = useNow()

const { formatToCurrencyString } = useCurrency()

type ColumnId =
  | "name"
  | "transactedTotal"
  | "lastTransactedAt"
  | "portalLink"
const columns: Array<
  TableColumn<
    ListMerchantGroupMerchantsResponseItem,
    ColumnId
  >
> = [
  {
    id: "name",
    header: "Name",
    getCellValue: (row) => row.merchantName,
  },
  {
    id: "transactedTotal",
    header: "Amount Transacted Total",
    getCellValue: (row) =>
      formatToCurrencyString(
        row.transactionTotal,
        row.currency
      ),
  },
  {
    id: "lastTransactedAt",
    header: "Last Transacted",
    getCellValue: (row) =>
      row.lastTransactedAt
        ? DateFns.formatDistance(
            row.lastTransactedAt,
            now.value,
            {
              addSuffix: true,
            }
          )
        : "",
  },
  {
    id: "portalLink",
    header: "",
    getCellValue: (row) => row.portalLink,
  },
]
</script>
